'use client'

import Link from 'next/link'
import { ImageWithFallback } from '@/components/ImageWithFallback'
import { IndustryTag } from './IndustryTag'
import { TechStackTag } from './TechStackTag'
import type { WorkCase } from '@/lib/api/work-cases'

interface CaseCardProps {
  data: WorkCase
}

export function CaseCard({ data }: CaseCardProps) {
  // 获取行业标签
  const industryTags = data.industries?.map(i => i.industry_tag_string).filter((i): i is string => !!i) || []

  // 获取技能标签
  const techStack = data.skills?.map(s => s.skill_string).filter((s): s is string => !!s) || []

  // 使用 case_name 作为标题，excerpt 作为描述
  const title = data.case_name || data.title_worktype || '未命名项目'
  const description = data.excerpt || '暂无描述'

  return (
    <Link
      href={`/case/${data.id}`}
      className="block group"
    >
      <article className="bg-white rounded-lg overflow-hidden border border-gray-100 transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        {/* Project Image */}
        <div className="aspect-video bg-gray-100 overflow-hidden">
          <ImageWithFallback
            src={data.images?.[0]?.file_address}
            alt={title}
            fallbackText={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Content Area */}
        <div className="p-4">
          {/* Project Title */}
          <h2 className="text-lg font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {title}
          </h2>

          {/* Project Description */}
          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
            {description}
          </p>

          {/* Tag Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Industry Tags */}
            {industryTags.filter(Boolean).slice(0, 2).map((tag) => (
              <IndustryTag key={`${data.id}-industry-${tag}`} tag={tag} />
            ))}

            {/* Tech Stack Tags */}
            <div className="flex flex-wrap gap-1">
              {techStack.filter(Boolean).slice(0, 3).map((tech) => (
                <TechStackTag key={`${data.id}-tech-${tech}`} tech={tech} />
              ))}
            </div>
          </div>
        </div>
      </article>
    </Link>
  )
}
